import React from 'react';
import styled, { css } from 'styled-components';
import { WeekdayStr } from 'rrule';
import { CalendarEvent, CalendarEventType, SemesterCalendarInfo } from 'shovel-lib/types';
import { DATE_FORMAT, isSameDate, now, timeDiffInMinutes, toMomentDate, toMomentUtcDate } from 'shovel-lib/utils/timeUtils';
import { Moment } from 'moment';

import { CalendarView } from '../../state/calendar/types';
import t from '../../i18n/t';
import {
  calculationPrecondition,
  constructStudyOrExtraTime,
  extractFreeTimeBlocks,
  minutesPopulatedWithEvents
} from 'shovel-lib/utils/calendarFreeBlocksUtils';
import {
  IS_PLANNABLE,
  IS_PLANNABLE_OVERDUE,
  PLANNABLE_EVENT_CLASS_NAME,
  PLANNABLE_EVENT_GROUP_ID,
  PLANNABLE_OVERDUE_EVENT_CLASS_NAME
} from './calendarConstants';
import { capitalize } from '../textUtils';
import { Draggable } from '@fullcalendar/interaction';
import { CourseSource } from '../../state/ics/reducers';
import { calendarStylingType } from '@state/settings/types';
import { OptionType } from '../../components/common/MoreOptionDropdown';

const LOCAL_WEEK_FORMAT = 'YYYY-w';

export type PlannedTaskMarkers = {
  [key: string]: number;
};

const toPlannableMonthDay = (date: Moment) => ({
  start: date.startOf('day').toDate(),
  end: date.add(1, 'days').toDate(),
  groupId: PLANNABLE_EVENT_GROUP_ID,
  display: 'background'
});

export const reduceEventsToDaysMap = (acc: any, event: any) => {
  const startDate = toMomentDate(event.start);
  if (!acc[startDate.format(DATE_FORMAT)]) {
    acc[startDate.format(DATE_FORMAT)] = toPlannableMonthDay(startDate);
  }
  const endDate = toMomentDate(event.end);
  if (!isSameDate(startDate, endDate)) {
    acc[endDate.format(DATE_FORMAT)] = toPlannableMonthDay(endDate);
  }
  return acc;
};

export const weeksBetweenDays = (start: Moment, end: Moment) => {
  if (start.isAfter(end)) return [];

  const weeks: string[] = [];
  // todo check should this be isoWeek
  // todo maybe WEEK_FORMAT (YYYY-W) should be replaced with YYYY-w
  const date = start.clone().startOf('week');
  while (!date.isAfter(end)) {
    const week = date.format(LOCAL_WEEK_FORMAT);
    if (!weeks.includes(week)) {
      weeks.push(week);
    }
    date.add(1, 'week');
  }
  return weeks;
};

export const datesWithinRange = (start: Moment, end: Moment) => {
  if (start.isAfter(end)) return [];

  const dates: string[] = [];
  const date = start.clone().startOf('day');
  while (!date.isAfter(end)) {
    dates.push(date.format(DATE_FORMAT));
    date.add(1, 'day');
  }
  return dates;
};

export const toDateInRequest = (date: string | Date) =>
  toMomentDate(date)
    .clone()
    .format(DATE_FORMAT);

export const isImportedEvent = (type: any) =>
  [CourseSource.MOODLE, CourseSource.BRIGHTSPACE, CourseSource.CANVAS].includes(type);

export const isCommitmentOrEvent = (type: CalendarEventType) =>
  isImportedEvent(type) || [CalendarEventType.COURSE, CalendarEventType.ACTIVITY, CalendarEventType.EVENT].includes(type);

export const isCommitmentOrEventOrAwake = (type: CalendarEventType) =>
  isCommitmentOrEvent(type) || type === CalendarEventType.AWAKE_TIME;

export const isCommute = (type: CalendarEventType) =>
  [CalendarEventType.COMMUTE_BEFORE, CalendarEventType.COMMUTE_AFTER].includes(type);

// The kinds of event a student has to get away from before they can study: classes, activities, their own events,
// imported LMS events, Google events and exams.
const isEventToBreakAfter = (type: CalendarEventType | string) =>
  isCommitmentOrEvent(type as CalendarEventType) || type === CalendarEventType.GOOGLE_EVENT || type === 'EXAM';

/**
 * Google Calendar events as the server sends them, labelled as Google events. They come without a type, so the break
 * after them was skipped, although they were meant to have it (a student, 2026-10-15).
 */
export const asGoogleEvents = <T extends object>(events: T[] | null | undefined) =>
  (events || []).map(event => ({ ...event, type: CalendarEventType.GOOGLE_EVENT }));

/**
 * Where each busy class, activity, event, exam and Google event between `from` and `to` ends: Auto-plan leaves a break
 * after it before study starts. An event with a commute after it is left out: the commute is the time to get to a
 * seat, and the free time already starts where it ends. Events marked free don't take time from study, so they need no
 * break.
 */
export const eventEndsToBreakAfter = (
  events: Pick<CalendarEvent, 'type' | 'end' | 'busy' | 'allDay'>[],
  commuteTimes: Pick<CalendarEvent, 'type' | 'start'>[],
  from: Date | string,
  to: Date | string
): Date[] => {
  const rangeStart = toMomentDate(from).valueOf();
  const rangeEnd = toMomentDate(to).valueOf();
  const commutesAfter = new Set(
    commuteTimes
      .filter(commute => commute.type === CalendarEventType.COMMUTE_AFTER)
      .map(commute => toMomentDate(commute.start).valueOf())
  );
  const ends = events
    .filter(event => event && event.busy && !event.allDay && isEventToBreakAfter(event.type))
    .map(event => toMomentDate(event.end).valueOf())
    .filter(end => end > rangeStart && end < rangeEnd && !commutesAfter.has(end));
  return Array.from(new Set(ends))
    .sort((a, b) => a - b)
    .map(end => new Date(end));
};

export const calculateStudyAndExtraTimeEvents = (
  calendarInfo: { calendarStartDate: Moment; calendarEndDate: Moment },
  semesterCalendarInfo: SemesterCalendarInfo,
  events: CalendarEvent[],
  awakeTimeEvents: CalendarEvent[]
) => {
  const { calendarStartDate, calendarEndDate } = calendarInfo;
  const { calendarMinDate, calendarMaxDate } = semesterCalendarInfo;

  const from = calendarStartDate.isAfter(calendarMinDate) ? calendarStartDate : calendarMinDate;
  const to = calendarEndDate.isBefore(calendarMaxDate) ? calendarEndDate : calendarMaxDate;

  if (!calculationPrecondition(from, to, awakeTimeEvents)) return [];

  const populatedMinutes = minutesPopulatedWithEvents(events, awakeTimeEvents, from, to);

  return extractFreeTimeBlocks(populatedMinutes).map(block =>
    constructStudyOrExtraTime(block, semesterCalendarInfo, from.toDate(), now())
  );
};

export const createDateRangeFilter = (from: Moment, to: Moment) => (e: CalendarEvent) => {
  const eventStart = toMomentDate(e.start);
  const eventEnd = toMomentDate(e.end);
  return (
    eventStart.isBetween(from, to, undefined, '[]') ||
    eventEnd.isBetween(from, to, undefined, '[]') ||
    from.isBetween(eventStart, eventEnd, undefined, '[]')
  );
};

export const awakeTimesFilter = (e: CalendarEvent) => e.type === CalendarEventType.AWAKE_TIME;

export const studyTimeFilter = (e: CalendarEvent) => e.type === CalendarEventType.STUDY_TIME;

/**
 * What is left of a block the time now is inside: it starts at `from` instead. Nothing can be planned in the part that
 * has gone, so a drag marks a block only from the now line down, and its Plan button plans only what is left (Petr).
 * A block not under way comes back as it is.
 */
export const fromNowOn = <T extends { start: any; end: any }>(event: T, from: Moment = now()): T => {
  const start = toMomentDate(event.start);
  const end = toMomentDate(event.end);
  return start.isBefore(from) && end.isAfter(from) ? { ...event, start: from.toDate() } : event;
};

/** Planning from now begins at the next 5 minutes, not at a stray minute like 14:07. */
const PLAN_FROM_NOW_STEP_MINUTES = 5;

/**
 * Where planning in a block under way begins: the next 5 minutes, or now when that would be past the block's end. The
 * block says how much time is left from there, as its Plan dialog does.
 */
export const planningStartFromNow = (end: any, from: Moment = now()) => {
  const at = from.clone().startOf('minute');
  const over = at.minutes() % PLAN_FROM_NOW_STEP_MINUTES;
  if (over > 0) at.add(PLAN_FROM_NOW_STEP_MINUTES - over, 'minutes');
  return at.isBefore(toMomentDate(end)) ? at : from;
};

const planFromNow = (event: any) => fromNowOn(event, planningStartFromNow(event.end));

export const extraTimeFilter = (e: CalendarEvent) => e.type === CalendarEventType.EXTRA_TIME;

/**
 * Plannable study block is study time event which meets these conditions:
 * - is in future and is visible on calendar
 * - is before dragged task due date
 * - doesn't have dragged task already planned
 *
 * @param from    - start date of the current calendar view or now (latest between these two)
 * @param to      - due date of the task
 */
export const createPlannableStudyBlocksFilter = (from: Moment, to: Moment) => (e: CalendarEvent) => {
  // if (!to.isAfter(from)) return false;

  const isStudy = e.type === CalendarEventType.STUDY_TIME;

  const eventStart = toMomentUtcDate(e.start);
  const eventEnd = toMomentUtcDate(e.end);
  // if task does not have dueDate than study block should be colored purple
  const isInsideGivenRange = to
    ? eventStart.isBetween(from, to, undefined, '[]') ||
      eventEnd.isBetween(from, to, undefined, '[]') ||
      to.isBetween(eventStart, eventEnd, undefined, '[]')
    : true;

  if (isStudy && eventEnd.isAfter(from)) {
    return isInsideGivenRange ? IS_PLANNABLE : IS_PLANNABLE_OVERDUE;
  }
  return null;
};

export const toUpdateEventDto = (calendarInfo: any, newEnd?: any) => {
  const {
    id,
    start,
    end,
    title,
    allDay,
    extendedProps: {
      colorHex,
      savedColorHex,
      description,
      commuteBefore,
      commuteAfter,
      location,
      type,
      isRecurring,
      originalEventStart,
      busy,
      repeat
    }
  } = calendarInfo;

  return {
    id,
    title,
    start,
    end: newEnd || end,
    description,
    commuteAfter,
    commuteBefore,
    location,
    type,
    allDay,
    busy,
    repeat,
    isRecurring,
    originalEventStart,
    // A custom event's picked colour, not the darker one dark mode draws (calendarEventConverters).
    colorHex: savedColorHex ?? colorHex
  };
};

export const shouldResetSliders = (event: CalendarEvent, eventFromState: CalendarEvent) => {
  const duration = timeDiffInMinutes(event.start, event.end);
  const totalPlanned = eventFromState.plannedTasks!.map(t => t.plannedMinutes).reduce((a: number, b: number) => a + b, 0);
  return duration < totalPlanned;
};

export const getFirstTwoDayLetters = (date: Date | string) =>
  toMomentDate(date)
    .format('dd')
    .toUpperCase() as WeekdayStr;

export const draggableStyle = css`
  cursor: grab;
  transition: box-shadow 0.2s ease;

  &:active {
    cursor: grabbing;
  }
  &:hover {
    box-shadow: 0px 2px 2px ${props => props.theme.boxShadow}, 0px 0px 2px ${props => props.theme.boxShadow};
  }
`;

export const PlannedTimeOverlay = styled.div<{ percent: number }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${props => props.percent}%;
`;

export const calendarViewsHeaderDateFormat = new Map([
  [CalendarView.MONTH, 'ddd'],
  [CalendarView.LIST, 'ddd DD'],
  [CalendarView.WEEK, 'ddd DD'],
  [CalendarView.CYCLIC, 'ddd DD']
]);

export const mapToPlanBlockEvent = (event: any) => {
  const {
    id,
    start,
    end,
    extendedProps: { type, plannedTasks, passed, isRecurring, isLms, dayOfWeek, repeat }
  } = event;
  return {
    id: id !== '' ? id : undefined,
    start,
    end,
    type,
    isLms,
    plannedTasks: plannedTasks || [],
    passed,
    isRecurring,
    dayOfWeek,
    repeat
  };
};

export const getEventContextMenuOptions = (
  calendarEvent: any,
  { openPlanBlockDialog, deletePlannedTask, deleteEvent, duplicatePlannedTask }
) => {
  const event = mapToPlanBlockEvent(calendarEvent);
  let options: OptionType[] = [];
  switch (event.type) {
    case CalendarEventType.PLANNED_TASK: {
      // Unplan alone, so the card shows its icon and acts on one click rather than opening a menu
      // for a single entry. Duplicate stays on the planned task's dialog.
      options = [
        {
          value: t.UNPLAN,
          label: t.UNPLAN,
          icon: 'event_busy',
          onClick: () => deletePlannedTask(Number(event.id))
        }
      ];
      break;
    }
    case CalendarEventType.STUDY_TIME: {
      options = [
        {
          value: t.PLAN_THIS_BLOCK,
          label: t.PLAN_THIS_BLOCK,
          icon: 'calendar_today',
          onClick: () => openPlanBlockDialog({ event: planFromNow(event) })
        }
      ];
      break;
    }
    default: {
      options = [{ value: t.DELETE, label: t.DELETE, icon: 'delete_outline', onClick: () => deleteEvent(event) }];
      break;
    }
  }
  return options;
};

export const removeDraggingClasses = event => {
  event.groupId = '';
  const classIndex = event.classNames.indexOf(PLANNABLE_EVENT_CLASS_NAME);
  const classIndexOverdue = event.classNames.indexOf(PLANNABLE_OVERDUE_EVENT_CLASS_NAME);

  if (classIndex !== -1) {
    event.classNames.splice(classIndex, 1);
  }
  if (classIndexOverdue !== -1) {
    event.classNames.splice(classIndexOverdue, 1);
  }
};

export const addDraggingClass = (event, groupId, className) => {
  event.groupId = groupId;
  event.classNames = [...(event.classNames || []), className];
};

// `start`: the event's own day, which a weekly repeat on that day alone doesn't list in `onDays`, so "Weekly" said
// which day only once there were two (Petr, 2026-10-09: "Weekly on Tu").
export const getRecurrenceText = (options, start?: Date | string) => {
  if (!options) return null;
  if (options.interval === 0) return t.ON_SELECTED_DAYS;

  let text =
    !options.interval || options.interval === 1
      ? t.WEEKLY
      : `${t.REPEATS_EVERY} ${options.interval} ${t.WEEKS.toLowerCase()}`;
  const onDays = options.onDays?.length ? options.onDays : start ? [getFirstTwoDayLetters(start)] : undefined;
  if (onDays) {
    const locale = toMomentDate().localeData();
    const allWeekdays = [...locale.weekdaysMin()];
    const shiftedWeekdays = allWeekdays.splice(0, locale.firstDayOfWeek());
    const weekdays = allWeekdays.concat(shiftedWeekdays).filter(d => onDays.map(capitalize).includes(d));

    if (weekdays.length > 0) {
      text += ` ${t.ON} ${weekdays.join(', ')}`;
    }
  }
  return text;
};

/**
 * Repeat pattern has changed if there wasn't a pattern and was added, or there was a pattern but was removed
 * or there was a pattern but some of the values were changed
 * No need to check if old and new are both undefined
 */
export const hasRepeatPatternChanged = (oldRepeat, newRepeat) =>
  !!oldRepeat !== !!newRepeat ||
  (oldRepeat &&
    newRepeat &&
    (oldRepeat.interval !== newRepeat.interval ||
      oldRepeat.onDays?.length !== newRepeat.onDays?.length ||
      //@ts-ignore
      (oldRepeat.onDays && !oldRepeat.onDays.every(d => newRepeat.onDays?.includes(d))) ||
      oldRepeat.includeDays?.length !== newRepeat.includeDays?.length ||
      //@ts-ignore
      (oldRepeat.includeDays && !oldRepeat.includeDays.every(d => newRepeat.includeDays?.includes(d))) ||
      oldRepeat.untilOption !== newRepeat.untilOption ||
      !toMomentDate(oldRepeat.until).isSame(newRepeat.until, 'date')));

/** How near the calendar's edges something dragged onto it has to come before it scrolls: the top, and the rest. */
const TOP_SCROLL_EDGE_PX = 30;
const SCROLL_EDGE_PX = 80;

export const extendDraggableItem = (draggableItem: Draggable) => {
  const scroller: any = draggableItem.dragging.autoScroller;
  scroller.edgeThreshold = SCROLL_EDGE_PX;
  scroller.maxVelocity = 1200;
  // Scrolling up began over the first hour on screen, too soon to plan there (Petr, 2026-10-09). FullCalendar has one
  // distance for every side, so the top gets its own: it scrolls up only this close, speeding up from nothing there.
  const computeBestEdge = scroller.computeBestEdge.bind(scroller);
  scroller.computeBestEdge = (left: number, top: number) => {
    const edge = computeBestEdge(left, top);
    return edge?.name === 'top' && edge.distance > TOP_SCROLL_EDGE_PX ? null : edge;
  };
  const handleSide = scroller.handleSide.bind(scroller);
  scroller.handleSide = (edge: { name: string }, seconds: number) => {
    scroller.edgeThreshold = edge.name === 'top' ? TOP_SCROLL_EDGE_PX : SCROLL_EDGE_PX;
    handleSide(edge, seconds);
    scroller.edgeThreshold = SCROLL_EDGE_PX;
  };
};

export const calendarStylingOptions = theme => ({
  [calendarStylingType.WHITE]: {
    name: t.WHITE,
    backgroundFunction: color => theme.background
  },
  [calendarStylingType.GRAY]: {
    name: t.GRAY,
    backgroundFunction: color => theme.activitiesColor
  },
  [calendarStylingType.LIGHT]: {
    name: '10%',
    backgroundFunction: theme.commitmentColorFunction
  },
  [calendarStylingType.FULL]: {
    name: '20%',
    backgroundFunction: theme.courseColorFunction
  }
});

export function getAwakeTimeRange(
  events: CalendarEvent[]
): { earliest: string | null; latest: string | null; duration: number } {
  const awakeEvents = events.filter(e => e.type === CalendarEventType.SLEEP_TIME);

  if (awakeEvents.length === 0) {
    return { earliest: null, latest: null, duration: 0 };
  }

  const { earliest, latest } = awakeEvents.reduce(
    (acc, event) => {
      const startHour = Math.floor(toNormalizedHour(event.start));
      const endHour = Math.ceil(toNormalizedHour(event.end));

      if (acc.earliest === null || startHour < acc.earliest) acc.earliest = startHour;
      if (acc.latest === null || endHour > acc.latest) acc.latest = endHour;

      return acc;
    },
    { earliest: null as number | null, latest: null as number | null }
  );

  const formatHour = (hour: number | null): string | null => {
    if (hour === null) return null;
    return `${String(hour).padStart(2, '0')}:00`;
  };

  return {
    earliest: formatHour(earliest),
    latest: formatHour(latest),
    duration: latest !== null && earliest !== null ? latest - earliest : 0
  };
}

const toNormalizedHour = (date: string | Date): number => {
  const momentDate = toMomentDate(date);
  let hour = momentDate.hour(); // 0–23
  const minute = momentDate.minute();

  // If it's before 4am, treat as next day (24+)
  if (hour < 4) hour += 24;

  // Add decimal if you want later rounding logic
  return hour + minute / 60;
};
